import React, { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import '@fontsource-variable/geist/wght.css';
import { Users, Palette, Signpost } from 'lucide-react';
import { IconDeviceMobileBolt, IconGauge } from '@tabler/icons-react';
import ConfigSectionSidebarEdge from '../config-hub/ConfigSectionSidebarEdge';

function DispositivosSubmenuIcon(props) {
  const { strokeWidth = 1.75, ...rest } = props;
  return <IconDeviceMobileBolt stroke={strokeWidth} {...rest} />;
}

function MedicoesSubmenuIcon(props) {
  const { strokeWidth = 1.75, ...rest } = props;
  return <IconGauge stroke={strokeWidth} {...rest} />;
}

const GO_SUBMENU_ITEMS = [
  { id: 'usuarios', label: 'Usuários', Icon: Users },
  { id: 'dispositivos', label: 'Dispositivos', Icon: DispositivosSubmenuIcon },
  { id: 'customizacao', label: 'Customização', Icon: Palette },
  { id: 'processos', label: 'Processos', Icon: Signpost },
  { id: 'medicoes', label: 'Medições', Icon: MedicoesSubmenuIcon },
];

function GoSidebarTooltipPortal({ anchorRect, text }) {
  if (!anchorRect || !text) return null;

  return createPortal(
    <span
      className="process-sidebar-tooltip-portal"
      style={{
        top: anchorRect.top + anchorRect.height / 2,
        left: anchorRect.right + 10,
      }}
      role="tooltip"
    >
      {text}
    </span>,
    document.body
  );
}

function useGoSidebarTooltip(text) {
  const anchorRef = useRef(null);
  const [anchorRect, setAnchorRect] = useState(null);

  const showTooltip = useCallback(() => {
    if (!text) return;
    const el = anchorRef.current;
    if (!el) return;
    setAnchorRect(el.getBoundingClientRect());
  }, [text]);

  const hideTooltip = useCallback(() => {
    setAnchorRect(null);
  }, []);

  return { anchorRef, anchorRect, showTooltip, hideTooltip };
}

function GoSubmenuItem({ id, label, Icon, active, collapsed, onSelect }) {
  const labelRef = useRef(null);
  const [truncatedHover, setTruncatedHover] = useState(false);

  const showTooltip = collapsed || truncatedHover;
  const { anchorRef, anchorRect, showTooltip: openTooltip, hideTooltip } = useGoSidebarTooltip(label);

  const handleMouseEnter = useCallback(() => {
    if (collapsed) {
      openTooltip();
      return;
    }
    const el = labelRef.current;
    const isTruncated = Boolean(el && el.scrollWidth > el.clientWidth + 1);
    setTruncatedHover(isTruncated);
    if (isTruncated) openTooltip();
  }, [collapsed, openTooltip]);

  const handleMouseLeave = useCallback(() => {
    setTruncatedHover(false);
    hideTooltip();
  }, [hideTooltip]);

  const setButtonRef = useCallback(
    (node) => {
      anchorRef.current = node;
    },
    [anchorRef]
  );

  return (
    <>
      <button
        ref={setButtonRef}
        type="button"
        className={['process-submenu-item', active ? 'active' : ''].filter(Boolean).join(' ')}
        onClick={() => onSelect(id)}
        aria-label={collapsed ? label : undefined}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleMouseEnter}
        onBlur={handleMouseLeave}
      >
        <Icon size={17} strokeWidth={1.75} aria-hidden />
        <span ref={labelRef} className="process-submenu-label">
          {label}
        </span>
      </button>
      <GoSidebarTooltipPortal anchorRect={showTooltip ? anchorRect : null} text={label} />
    </>
  );
}

export default function GoConfigSidebar({ activeSubmenu, onSelectSubmenu }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <aside
      className={`process-sidebar${sidebarCollapsed ? ' is-collapsed' : ''}`}
      aria-label="Go — configurações"
    >
      <ConfigSectionSidebarEdge
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed((v) => !v)}
      />
      <nav className="process-submenu">
        {GO_SUBMENU_ITEMS.map((item) => (
          <GoSubmenuItem
            key={item.id}
            {...item}
            active={activeSubmenu === item.id}
            collapsed={sidebarCollapsed}
            onSelect={onSelectSubmenu}
          />
        ))}
      </nav>
    </aside>
  );
}
